/**
 * CssEditor Component for UI-Model-1
 *
 * Custom CSS editor for adding custom styles to the runtime.
 * Adapted from existing UI-Model CustomCSS.
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import Button from 'Components/DesignSystem/Button/Button';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { useModule } from '../../contexts/ModuleContext';

interface CssEditorProps {
  id?: string;
  projectId: string;
}

export interface CustomCSSModel {
  globalStyles: string;
}

// Simple CSS validator
const validateCSS = (css: string): { isValid: boolean; error?: string } => {
  if (!css || css.trim() === '') {
    return { isValid: true };
  }

  // Basic validation: check for balanced braces
  let braceCount = 0;
  for (const char of css) {
    if (char === '{') braceCount++;
    if (char === '}') braceCount--;
    if (braceCount < 0) {
      return { isValid: false, error: 'Unmatched closing brace' };
    }
  }

  if (braceCount !== 0) {
    return { isValid: false, error: 'Unmatched opening brace' };
  }

  return { isValid: true };
};

export const CssEditor = ({ id, projectId }: CssEditorProps) => {
  const baseId = id || 'css-editor';
  const { state, dispatch } = useUIModelState();
  const { updateCustomCss, getCustomCss } = useUIModelApi();
  const { hasEditPermission } = useModule();

  const [cssValue, setCssValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load CSS on mount
  useEffect(() => {
    const loadCss = async () => {
      if (!state.model?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getCustomCss(projectId, state.model.id);
        if (response?.done && response?.data?.globalStyles) {
          setCssValue(response.data.globalStyles);
        } else {
          setCssValue('');
        }
      } catch (err) {
        console.error('Failed to load CSS:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCss();
  }, [projectId, state.model?.id, getCustomCss]);

  const handleCssChange = (value: string) => {
    setCssValue(value);
    const validation = validateCSS(value);
    setError(validation.isValid ? null : validation.error || 'Invalid CSS');
  };

  const handleSave = async () => {
    if (!hasEditPermission()) {
      toast.error("You don't have permission to update this model");
      return;
    }

    if (!state.model?.id) {
      toast.error('No model loaded');
      return;
    }

    if (error) {
      toast.error('Please fix CSS errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateCustomCss(projectId, state.model.id, { globalStyles: cssValue });
      if (result?.done) {
        // Update state
        dispatch({
          type: 'UPDATE_CUSTOM_CSS',
          payload: { css: cssValue },
        });
        toast.success('Custom CSS updated successfully');
      }
    } catch (err) {
      console.error('Failed to save CSS:', err);
      toast.error('Failed to save CSS');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div
        id={baseId}
        data-testid={baseId}
        className="tw-p-8 tw-w-full tw-flex tw-items-center tw-justify-center"
      >
        <Typography variant="body-primary" className="tw-text-ink-secondary">Loading CSS...</Typography>
      </div>
    );
  }

  /* Dark-mode migration (2026-09-10): the panel painted light-only literals — a
     `grey-300` textarea with a `blue-200` focus ring, a `blue-900` Save, a `grey-700`
     Clear — so under `data-theme="dark"` it sat as a grey slab on the dark page. It is
     now DS `Textarea` (field chrome from the shared seam; the validation error rides
     `helperText`, announced as an alert), DS `Button`s and DS `Typography`. Behaviour
     — the brace validator, the permission gate, the manual save that bypasses the
     SaveQueue — is untouched. Monospace is applied through the wrapper so the field's
     own chrome classes stay exactly the DS set. */
  return (
    <div id={baseId} data-testid={baseId} className="tw-p-8 tw-w-full">
      <Typography id={`${baseId}-title`} variant="title-header" as="h3" className="tw-text-ink tw-mb-3">
        Add custom styles to the runtime
      </Typography>

      <div className="tw-w-full tw-max-w-3xl">
        <Textarea
          /* The panel heading IS the field's name — so no second, smaller FieldTitle;
             the textarea is labelled by the heading instead. */
          label=""
          aria-labelledby={`${baseId}-title`}
          id={`${baseId}-textarea`}
          data-testid={`${baseId}-textarea`}
          value={cssValue}
          onChange={(value) => handleCssChange(value)}
          placeholder="Enter your CSS here..."
          rows={14}
          error={!!error}
          helperText={error ?? ''}
          spellCheck={false}
          className="[&_textarea]:tw-font-mono"
        />

        <div className="tw-mt-4 tw-flex tw-gap-3">
          <Button
            variant="primary"
            id={`${baseId}-save-btn`}
            data-testid={`${baseId}-save-btn`}
            onClick={handleSave}
            disabled={isSaving || !!error}
            loading={isSaving}
          >
            Save
          </Button>

          <Button
            variant="secondary"
            id={`${baseId}-clear-btn`}
            data-testid={`${baseId}-clear-btn`}
            onClick={() => {
              setCssValue('');
              setError(null);
            }}
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CssEditor;
